/**
 * <linked-product-swatches>
 *
 * The color swatches and size pills on the PDP point at *separate products*, so picking
 * one is a full page navigation rather than a variant switch. On a throttled connection
 * the browser keeps the current page on screen for several seconds while the next one
 * loads, and with no feedback that is indistinguishable from the page having frozen -
 * which is exactly what customers reported.
 *
 * This element does not change what the links do. It only makes the wait legible: the
 * tapped swatch takes the selected ring and a spinner, the "Color:" label updates
 * optimistically, and the choice is announced to assistive technology.
 *
 * Markup contract (see snippets/linked-product-swatches.liquid):
 *   - links carry `data-swatch-label` with the name to show while loading
 *   - the label's value span carries `data-swatch-current-value`
 *   - a visually hidden live region carries `data-swatch-status`
 *   - the host carries `data-loading-template`, a translated string containing `[title]`
 */

const PENDING = 'is-pending';

class LinkedProductSwatches extends HTMLElement {
  connectedCallback() {
    // Bound class fields give a stable reference, so re-connecting (e.g. after a morph)
    // cannot register the same listener twice.
    this.addEventListener('click', this.#handleClick);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * A click only means "this document is about to be replaced" for a plain left click.
   * Modified clicks, middle clicks and target="_blank" all leave the current page where
   * it is, so they must not put the swatch into a loading state that never resolves.
   *
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[data-swatch-label]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;
    if (link.target && link.target !== '_self') return;

    this.#markPending(link);
  };

  /** @param {PageTransitionEvent} event */
  #handlePageShow = (event) => {
    // Restored from the back/forward cache - whatever was loading is no longer relevant.
    if (event.persisted) this.#clearPending();
  };

  /**
   * Tapping a second swatch while the first is still loading is legitimate: the browser
   * abandons the in-flight navigation and starts the new one. Move the state rather than
   * blocking the interaction.
   *
   * @param {HTMLAnchorElement} link
   */
  #markPending(link) {
    this.#clearPending();

    link.classList.add(PENDING);
    link.setAttribute('aria-busy', 'true');
    this.dataset.pending = '';

    const label = link.dataset.swatchLabel;
    if (!label) return;

    // Optimistic: name the choice immediately instead of after the next page paints.
    const currentValue = this.querySelector('[data-swatch-current-value]');
    if (currentValue) currentValue.textContent = label;

    const status = this.querySelector('[data-swatch-status]');
    if (status) {
      const template = this.dataset.loadingTemplate || 'Loading [title]';
      status.textContent = template.replace('[title]', label);
    }
  }

  #clearPending() {
    delete this.dataset.pending;

    for (const element of this.querySelectorAll(`.${PENDING}`)) {
      element.classList.remove(PENDING);
      element.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('linked-product-swatches')) {
  customElements.define('linked-product-swatches', LinkedProductSwatches);
}
